import { generatePath, useNavigate } from "react-router-dom";

import type { UserAppointment } from "~/api";
import { ROUTES } from "~/router/constants";
import { Button } from "~/shared";
import { errorToast, icons } from "~/ui";
import { tw } from "~/utils";
import { formatLinkAvailableDate, isVisitJoinable } from "../utils/visit-date";
import { AppointmentSummary } from "./AppointmentSummary";

interface UpcomingVisitItemProps {
  visit: UserAppointment;
}

export const UpcomingVisitItem = ({ visit }: UpcomingVisitItemProps) => {
  const navigate = useNavigate();
  const joinable = isVisitJoinable(visit.date, visit.timezone);

  const handleCancel = () => {
    navigate(generatePath(ROUTES.hubCancelVisit, { appointmentId: visit.id }));
  };

  const handleJoin = () => {
    if (!visit.join_link) {
      errorToast();
      return;
    }
    window.open(visit.join_link, "_blank", "noopener,noreferrer");
  };

  const annotation = joinable
    ? "We recommend joining 10 minutes early."
    : `A link will be available on ${formatLinkAvailableDate(visit.date, visit.timezone)}. Join 10 minutes early.`;

  const cancelLabel = joinable ? "Late cancel" : "Cancel/reschedule";

  return (
    <div
      className={tw(
        "bg-placebo-lavender text-indigo-charcoal flex flex-col gap-3 rounded-lg p-5 md:flex-row md:items-start md:gap-4 lg:flex-col lg:items-stretch lg:gap-3 xl:flex-row xl:items-start xl:gap-4",
        joinable && "border-violet80 border-3",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <AppointmentSummary visit={visit}>
          <p className="hidden text-base italic leading-6 md:block">
            {annotation}
          </p>
        </AppointmentSummary>
        <p className="text-center text-sm italic leading-5 md:hidden">
          {annotation}
        </p>
      </div>

      <div className="flex flex-col gap-3 md:min-w-40 md:items-end md:gap-2 lg:w-full lg:min-w-0 lg:items-start lg:gap-3 xl:w-auto xl:min-w-40 xl:items-end xl:gap-2">
        {joinable && (
          <Button
            onClick={handleJoin}
            className="w-full gap-2 py-3 md:w-auto md:shrink-0 md:px-5 lg:w-full xl:w-auto xl:px-10"
            size="sm"
          >
            <icons.outline.VideoCameraIcon className="size-5" />
            Join visit
          </Button>
        )}
        <button
          type="button"
          onClick={handleCancel}
          className="text-indigo-charcoal cursor-pointer self-center font-sans text-sm font-normal underline md:text-base"
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  );
};
